// src/pages/admin/BancosPage.tsx
import React, { useEffect, useState } from "react";
import { getBanks, createBank, updateBank, deleteBank } from "../../services/api";
import type { Banco } from "../../services/api";

const BancosPage: React.FC = () => {
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [nombre, setNombre] = useState("");
  const [cuenta, setCuenta] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNombre, setEditNombre] = useState("");
  const [editCuenta, setEditCuenta] = useState("");

  const [loadingList, setLoadingList] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
// src/pages/admin/BancosPage.tsx
useEffect(() => {
  const t =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  console.log(
    "[BANCOS] init → token?",
    t ? t.slice(0, 12) + "…" : "NONE",
    "| API_URL:",
    process.env.REACT_APP_API_URL
  );

  fetchBancos();
}, []);

  // 1️⃣ Obtener bancos
  const fetchBancos = async () => {
    setLoadingList(true);
    setErrorMsg(null);
    try {
      const list = await getBanks(); // ya devuelve Banco[]
      setBancos(list);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || "Error";
      setErrorMsg(`❌ No se pudieron cargar los bancos (${status ?? "??"}): ${msg}`);
      console.error("Banks error:", err?.response || err);
    } finally {
      setLoadingList(false);
    }
  };

  // 2️⃣ Agregar banco
  const agregarBanco = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim() || !cuenta.trim()) return;

    setErrorMsg(null);
    setLoadingAction(true);
    try {
      await createBank(nombre.trim(), cuenta.trim());
      setNombre("");
      setCuenta("");
      await fetchBancos();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || "Error";
      setErrorMsg(`❌ Error al agregar banco (${status ?? "??"}): ${msg}`);
      console.error(err);
    } finally {
      setLoadingAction(false);
    }
  };

  // 3️⃣ Eliminar banco
  const eliminarBanco = async (id: string) => {
    if (!window.confirm("¿Eliminar este banco?")) return;
    setErrorMsg(null);
    setLoadingAction(true);
    try {
      await deleteBank(id);
      setBancos((prev) => prev.filter((b) => b.id !== id));
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || "Error";
      setErrorMsg(`❌ Error al eliminar banco (${status ?? "??"}): ${msg}`);
      console.error(err);
    } finally {
      setLoadingAction(false);
    }
  };

  // 4️⃣ Iniciar / cancelar edición
  const startEdit = (banco: Banco) => {
    setEditingId(banco.id);
    setEditNombre(banco.nombre);
    setEditCuenta(banco.cuenta);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditNombre("");
    setEditCuenta("");
  };

  // 5️⃣ Guardar edición
  const saveEdit = async () => {
    if (!editingId) return;
    if (!editNombre.trim() || !editCuenta.trim()) return;

    setErrorMsg(null);
    setLoadingAction(true);
    try {
      await updateBank(editingId, { nombre: editNombre.trim(), cuenta: editCuenta.trim() });
      setBancos((prev) =>
        prev.map((b) =>
          b.id === editingId ? { ...b, nombre: editNombre.trim(), cuenta: editCuenta.trim() } : b
        )
      );
      cancelEdit();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || "Error";
      setErrorMsg(`❌ Error al actualizar banco (${status ?? "??"}): ${msg}`);
      console.error(err);
    } finally {
      setLoadingAction(false);
    }
  };

  useEffect(() => {
    fetchBancos();
  }, []);

  return (
    <div className="relative w-full overflow-x-clip">
      {/* acentos de fondo estilo dashboard */}
      <div className="pointer-events-none absolute inset-x-0 -top-24 -z-10 mx-auto h-64 max-w-4xl rounded-full bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-fuchsia-500/10 blur-3xl" />

      <div className="mx-auto max-w-[1100px] sm:max-w-3xl lg:max-w-5xl xl:max-w-6xl px-3 sm:px-6 py-6 sm:py-8">
        <header className="mb-4">
          <h2 className="text-3xl font-bold text-white drop-shadow-sm">🏦 Registrar Cuenta / Banco</h2>
        </header>

        {errorMsg && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-300/60 bg-red-600/30 px-3 py-2 text-sm text-red-100">
            <span>❌</span>
            <p className="leading-5">{errorMsg}</p>
          </div>
        )}

        {/* Card del formulario */}
        <section
          className="relative mx-auto max-w-xl overflow-hidden rounded-2xl bg-white/80 p-6 backdrop-blur ring-1 ring-slate-200 shadow-sm"
          aria-labelledby="form-title"
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600" />
          <h3 id="form-title" className="sr-only">Formulario de banco</h3>

          <form onSubmit={agregarBanco} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-800">
                Nombre del banco
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                disabled={loadingAction}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-800">
                Número de cuenta
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                value={cuenta}
                onChange={(e) => setCuenta(e.target.value)}
                required
                disabled={loadingAction}
              />
            </div>

            <button
              type="submit"
              className="w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 font-semibold text-white shadow transition hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loadingAction}
            >
              {loadingAction ? "Guardando…" : "➕ Guardar Banco"}
            </button>
          </form>
        </section>

        {/* Lista */}
        <section className="mx-auto mt-8 max-w-xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="text-lg">📋</span>
            <h3 className="text-xl font-semibold text-white drop-shadow-sm">Cuentas Registradas</h3>
          </div>

          {loadingList && <p className="text-white/90">Cargando bancos…</p>}

          {!loadingList && bancos.length === 0 && (
            <p className="text-white/90">No hay bancos registrados.</p>
          )}

          <ul className="space-y-3">
            {bancos.map((banco) => (
              <li
                key={banco.id}
                className="relative overflow-hidden rounded-xl border border-slate-200 bg-white/80 p-4 text-slate-800 shadow-sm backdrop-blur"
              >
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600" />
                {editingId === banco.id ? (
                  <>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <input
                        type="text"
                        className="rounded border px-2 py-1"
                        value={editNombre}
                        onChange={(e) => setEditNombre(e.target.value)}
                      />
                      <input
                        type="text"
                        className="rounded border px-2 py-1"
                        value={editCuenta}
                        onChange={(e) => setEditCuenta(e.target.value)}
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={loadingAction}
                        className="rounded bg-emerald-600 px-3 py-1 text-white hover:bg-emerald-700 disabled:opacity-60"
                      >
                        💾 Guardar
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="rounded bg-gray-500 px-3 py-1 text-white hover:bg-gray-600"
                      >
                        ✖️ Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p>
                      <strong>🏦 Banco:</strong> {banco.nombre || "—"}
                    </p>
                    <p>
                      <strong>🔢 Cuenta:</strong> {banco.cuenta || "—"}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(banco)}
                        className="rounded bg-yellow-500 px-3 py-1 text-white hover:bg-yellow-600"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => eliminarBanco(banco.id)}
                        disabled={loadingAction}
                        className="rounded bg-red-600 px-3 py-1 text-white hover:bg-red-700 disabled:opacity-60"
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default BancosPage;
